import { useState, useEffect } from "react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/layout/DashboardShell";
import { carrierNav } from "@/pages/carrier/nav";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, UserPlus, Trash2, Crown } from "lucide-react";

const ROLES = [["dispatcher", "Dispatcher"], ["driver", "Driver"], ["accountant", "Accountant"], ["fleet_manager", "Fleet Manager"]];

export default function Team() {
  const [members, setMembers] = useState(null);
  const [f, setF] = useState({ name: "", email: "", password: "", team_role: "dispatcher" });
  const [busy, setBusy] = useState(false);

  const fetch = () => api.get("/carrier/team").then((r) => setMembers(r.data));
  useEffect(() => { fetch(); }, []);
  if (!members) return <DashboardShell nav={carrierNav}><div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-xd-blue" size={32} /></div></DashboardShell>;

  const invite = async () => {
    setBusy(true);
    try { await api.post("/carrier/team/invite", f); toast.success(`${f.name} can now log in`); setF({ name: "", email: "", password: "", team_role: "dispatcher" }); fetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { try { await api.delete(`/carrier/team/${id}`); toast.success("Removed"); fetch(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <DashboardShell nav={carrierNav} title="Company Team">
      <p className="text-xd-muted -mt-4 mb-6">Invite your dispatcher, drivers and accountant with their own separate logins under your company.</p>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-3">
          {members.map((m) => (
            <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center justify-between bg-xd-paper border border-xd-border rounded-xl p-4">
              <div>
                <p className="font-semibold flex items-center gap-2">{m.name} {m.is_owner && <Crown size={14} className="text-xd-gold" />}</p>
                <p className="text-sm text-xd-muted">{m.email} · <span className="capitalize">{(m.team_role || "owner").replace("_", " ")}</span></p>
              </div>
              {!m.is_owner && <button data-testid={`del-member-${m.id}`} onClick={() => remove(m.id)} className="text-xd-dim hover:text-xd-red"><Trash2 size={16} /></button>}
            </div>
          ))}
        </div>
        <div className="bg-xd-paper border border-xd-border rounded-xl p-6 h-fit">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><UserPlus size={18} className="text-xd-blue" /> Invite member</h2>
          <div className="mt-4 space-y-3">
            <div><Label className="text-xd-muted text-sm">Name</Label><Input data-testid="inv-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 bg-xd-surface border-xd-border" /></div>
            <div><Label className="text-xd-muted text-sm">Email</Label><Input data-testid="inv-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1 bg-xd-surface border-xd-border" /></div>
            <div><Label className="text-xd-muted text-sm">Temp password</Label><Input data-testid="inv-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1 bg-xd-surface border-xd-border" /></div>
            <div><Label className="text-xd-muted text-sm">Role</Label>
              <Select value={f.team_role} onValueChange={(v) => setF({ ...f, team_role: v })}>
                <SelectTrigger data-testid="inv-role" className="mt-1 bg-xd-surface border-xd-border"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="inv-submit" onClick={invite} disabled={busy || !f.name || !f.email || !f.password} className="w-full bg-xd-blue hover:bg-xd-blueH text-white rounded-xl">{busy ? <Loader2 className="animate-spin" size={16} /> : "Send Invite"}</Button>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
